import React, { useState, useEffect, useCallback } from 'react';
import MeetingScheduler from './MeetingScheduler';
import { COMMON_TIMEZONES, localTimezone, tzLabel } from '../utils/timezone';

const API = process.env.REACT_APP_API_URL || '';
function authHeaders() {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/**
 * AvailabilitySection — lives in the Interviews sidepanel. Lets a recruiter
 * create a tokenized availability request for the candidate, see status, copy
 * the link, and view what the candidate selected.
 */
export default function AvailabilitySection({ candidateId, candidateName, candidateEmail, candidatePhone, candidatePhones, cardId, pipelineId, pipelineName, onSchedule }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);

  const load = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const url = `${API}/api/availability/candidate/${candidateId}${pipelineId ? `?pipeline_id=${pipelineId}` : ''}`;
      const r = await fetch(url, { headers: authHeaders() });
      setRequests(await r.json());
    } catch (e) { console.error('load availability failed', e); }
    finally { setLoading(false); }
  }, [candidateId, pipelineId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Availability</span>
        <button onClick={() => setBuilding(true)}
          className="text-[10px] text-emerald-700 hover:text-emerald-800 font-medium">
          + Request availability
        </button>
      </div>

      {/* Inline builder — expands in place when requesting availability */}
      {building && (
        <div className="my-1.5">
          <AvailabilityBuilder
            embedded
            candidateId={candidateId}
            candidateName={candidateName}
            pipelineId={pipelineId}
            pipelineName={pipelineName}
            onClose={() => setBuilding(false)}
            onCreated={() => load()}
          />
        </div>
      )}

      {loading && <p className="text-[11px] text-gray-400">Loading…</p>}
      {!loading && requests.length === 0 && (
        <p className="text-[11px] text-gray-400">No availability requests yet.</p>
      )}
      {requests.map(req => (
        <AvailabilityRequestRow key={req.id} req={req} onChanged={load}
          canSchedule={!!cardId}
          candidatePhone={candidatePhone}
          candidatePhones={candidatePhones}
          onSchedule={(slot) => onSchedule?.(slot)} />
      ))}
    </div>
  );
}

function AvailabilityRequestRow({ req, onChanged, canSchedule, onSchedule, candidatePhone, candidatePhones }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  // Build the candidate's known phone numbers, primary first.
  const phoneList = React.useMemo(() => {
    const out = [];
    const arr = Array.isArray(candidatePhones) ? candidatePhones : [];
    arr.forEach(p => { const v = (typeof p === 'string' ? p : p?.value)?.trim(); if (v) out.push({ value: v, label: `${v}${(p?.is_primary) ? ' (primary)' : ''}${p?.type ? ' · ' + p.type : ''}` }); });
    if (candidatePhone && !out.some(o => o.value === candidatePhone)) out.unshift({ value: candidatePhone, label: `${candidatePhone} (primary)` });
    return out;
  }, [candidatePhones, candidatePhone]);
  const [phoneChoice, setPhoneChoice] = useState(phoneList[0]?.value || '__custom__');
  const [phone, setPhone] = useState(phoneList[0]?.value || '');
  const [smsState, setSmsState] = useState('idle'); // idle | sending | sent | error
  const [smsError, setSmsError] = useState('');
  const link = `${window.location.origin}/availability/${req.token}`;
  const defaultMsg = `Hi — please pick your interview availability here: ${link}`;
  const [smsBody, setSmsBody] = useState(defaultMsg);
  const proposed = Array.isArray(req.proposed) ? req.proposed : [];
  // New response format: { ranges:[{windowId,date,start,end}], flexible:[windowId] }
  const sel = req.selected && typeof req.selected === 'object' && !Array.isArray(req.selected) ? req.selected : {};
  const chosenRanges = Array.isArray(sel.ranges) ? sel.ranges : [];
  const flexibleWins = Array.isArray(sel.flexible) ? sel.flexible : [];
  const flexibleDates = proposed.filter(p => flexibleWins.includes(p.id)).map(p => p.date);
  const responseCount = chosenRanges.length + flexibleWins.length;

  const copy = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const sendSms = async () => {
    if (!phone.trim()) return;
    setSmsState('sending'); setSmsError('');
    try {
      const r = await fetch(`${API}/api/availability/${req.id}/send-sms`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ phone: phone.trim(), body: smsBody.trim() }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'Failed'); }
      setSmsState('sent'); setTimeout(() => { setSmsOpen(false); setSmsState('idle'); }, 1500);
    } catch (err) { setSmsError(err.message); setSmsState('error'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this availability request?')) return;
    await fetch(`${API}/api/availability/${req.id}`, { method: 'DELETE', headers: authHeaders() });
    onChanged();
  };

  const fmt = (p) => {
    if (p.start && p.end && !p.date) {
      const s = new Date(p.start), e = new Date(p.end);
      return `${s.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${s.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–${e.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    }
    return `${new Date(p.date + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })} ${p.start}–${p.end}`;
  };

  const statusStyle = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    responded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    expired: 'bg-gray-100 text-gray-500 border-gray-200',
    cancelled: 'bg-gray-100 text-gray-400 border-gray-200',
  }[req.status] || 'bg-gray-100 text-gray-500 border-gray-200';

  return (
    <div className="border rounded bg-white overflow-hidden">
      <div className="px-2.5 py-2 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-gray-800 truncate">{req.title || 'Availability request'}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-medium ${statusStyle}`}>
              {req.status === 'responded' ? `${responseCount} marked` : req.status}
            </span>
          </div>
          <span className="text-[10px] text-gray-400">{proposed.length} times proposed</span>
        </div>
        {req.status === 'responded' && (
          <button onClick={() => setExpanded(x => !x)} className="text-[10px] text-emerald-700 hover:text-emerald-800 font-medium flex-shrink-0">
            {expanded ? 'Hide' : 'View'}
          </button>
        )}
        <button onClick={copy} title="Copy link" className="p-1 text-gray-400 hover:text-emerald-600 flex-shrink-0">
          {copied ? <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>}
        </button>
        <button onClick={() => setSmsOpen(s => !s)} title="Text link to phone" className="p-1 text-gray-400 hover:text-emerald-600 flex-shrink-0">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 3v-3z" /></svg>
        </button>
        <button onClick={remove} title="Delete" className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
        </button>
      </div>

      {smsOpen && (
        <div className="px-2.5 pb-2 border-t border-gray-100 pt-2 space-y-1.5">
          {phoneList.length > 0 && (
            <select value={phoneChoice}
              onChange={(e) => { setPhoneChoice(e.target.value); if (e.target.value !== '__custom__') setPhone(e.target.value); else setPhone(''); }}
              className="w-full text-[11px] border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500">
              {phoneList.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              <option value="__custom__">Custom number…</option>
            </select>
          )}
          {(phoneChoice === '__custom__' || phoneList.length === 0) && (
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 123 4567" autoFocus
              className="w-full text-[11px] border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
          )}
          <textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value)} rows={3}
            className="w-full text-[11px] border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
            placeholder="Message…" />
          <p className="text-[9px] text-gray-400">The availability link is added automatically if not included.</p>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-400 truncate">{phone ? `Text to ${phone}` : 'Enter a phone number'}</span>
            <button onClick={sendSms} disabled={smsState === 'sending' || !phone.trim()}
              className="text-[10px] px-2.5 py-1 rounded bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50 flex-shrink-0">
              {smsState === 'sending' ? 'Sending…' : smsState === 'sent' ? 'Sent!' : 'Text link'}
            </button>
          </div>
          {smsState === 'error' && <p className="text-[10px] text-red-500">{smsError || "Couldn't send. Check the number."}</p>}
        </div>
      )}

      {expanded && req.status === 'responded' && (
        <div className="px-2.5 pb-2 border-t border-gray-100 pt-2 space-y-1">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Candidate is free {req.timezone ? `· ${tzLabel(req.timezone)}` : ''}</span>
          {responseCount === 0 ? <p className="text-[11px] text-gray-400">Nothing marked.</p> : (
            <>
              {chosenRanges.map((r, i) => {
                const fmtRange = () => {
                  const d = new Date(r.date + 'T00:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
                  const t = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); const ap = h >= 12 ? 'PM' : 'AM'; const hh = ((h + 11) % 12) + 1; return `${hh}:${String(m).padStart(2, '0')} ${ap}`; };
                  return `${d} · ${t(r.start)}–${t(r.end)}`;
                };
                // Prefill MeetingScheduler with the candidate's exact range,
                // auto-capped to 45 min (recruiter fine-tunes with 5-min steps).
                const toPrefill = () => {
                  const [sh, sm] = r.start.split(':').map(Number);
                  const [eh, em] = r.end.split(':').map(Number);
                  const startMin = sh * 60 + sm, windowEnd = eh * 60 + em;
                  const capped = Math.min(startMin + 45, windowEnd);
                  const pad = (n) => String(n).padStart(2, '0');
                  return { date: r.date, startTime: r.start, endTime: `${pad(Math.floor(capped / 60))}:${pad(capped % 60)}` };
                };
                return (
                  <div key={i} className="text-[11px] text-gray-700 flex items-center gap-1.5 group/slot">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="flex-1">{fmtRange()}</span>
                    {canSchedule && (
                      <button onClick={() => onSchedule(toPrefill())}
                        className="opacity-0 group-hover/slot:opacity-100 text-[10px] text-emerald-700 hover:text-emerald-800 font-medium transition-opacity flex-shrink-0">
                        Schedule
                      </button>
                    )}
                  </div>
                );
              })}
              {flexibleDates.map((d, i) => (
                <div key={`f${i}`} className="text-[11px] text-gray-700 flex items-center gap-1.5 group/slot">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                  <span className="flex-1">{new Date(d + 'T00:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · <span className="text-amber-600 font-medium">flexible all day</span></span>
                  {canSchedule && (
                    <button onClick={() => onSchedule({ date: d, startTime: '09:00', endTime: '09:45' })}
                      className="opacity-0 group-hover/slot:opacity-100 text-[10px] text-emerald-700 hover:text-emerald-800 font-medium transition-opacity flex-shrink-0">
                      Schedule
                    </button>
                  )}
                </div>
              ))}
            </>
          )}
          {req.candidate_note && <p className="text-[11px] text-gray-500 italic mt-1">"{req.candidate_note}"</p>}
        </div>
      )}
    </div>
  );
}

/**
 * AvailabilityBuilder — modal to define proposed interviewer windows and
 * generate the request. Supports two modes:
 *   ranges — broad windows (day + start/end); candidate marks free within.
 *   slots  — discrete bookable slots generated from windows + slot length.
 */
/**
 * InterviewsSection — scheduling + scheduled-meetings list. Separate from
 * Availability. The form is collapsed by default; opens via the "+ Schedule
 * meeting" button OR an external prefill (from an availability response's
 * hover-Schedule). Lives below Availability in the panel.
 */
export function InterviewsSection({ candidateId, candidateName, candidateEmail, cardId, pipelineId, pipelineName, prefill, onPrefillConsumed }) {
  const [prefillState, setPrefillState] = useState(null);
  const [openForm, setOpenForm] = useState(0); // increment to signal "open the form"

  useEffect(() => {
    if (prefill) { setPrefillState(prefill); setOpenForm(n => n + 1); onPrefillConsumed?.(); }
  }, [prefill]); // eslint-disable-line

  if (!cardId) return null;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Interviews</span>
        <button onClick={() => { setPrefillState({ date: '', startTime: '', endTime: '' }); setOpenForm(n => n + 1); }}
          className="text-[10px] text-emerald-700 hover:text-emerald-800 font-medium">
          + Schedule meeting
        </button>
      </div>
      <MeetingScheduler
        embedded
        isOpen={true}
        onClose={() => {}}
        cardId={cardId}
        candidateId={candidateId}
        candidateName={candidateName}
        candidateEmail={candidateEmail}
        pipelineName={pipelineName}
        pipelineId={pipelineId}
        prefill={prefillState}
        openFormSignal={openForm}
        onMeetingCreated={() => setPrefillState(null)}
      />
    </div>
  );
}

export function AvailabilityBuilder({ candidateId, candidateName, pipelineId, pipelineName, onClose, onCreated, embedded = false }) {
  const [title, setTitle] = useState(pipelineName ? `Interview — ${pipelineName}` : 'Interview availability');
  const [timezone, setTimezone] = useState(localTimezone());
  // Broad windows the recruiter opens up: [{ id, date, start, end }]
  const [windows, setWindows] = useState([{ id: 'w1', date: '', start: '09:00', end: '12:00' }]);
  const [saving, setSaving] = useState(false);

  const addWindow = () => setWindows(w => [...w, { id: `w${Date.now()}`, date: '', start: '09:00', end: '17:00' }]);
  const updWindow = (id, patch) => setWindows(w => w.map(x => x.id === id ? { ...x, ...patch } : x));
  const delWindow = (id) => setWindows(w => w.filter(x => x.id !== id));

  // Proposed = the valid broad windows. Candidate marks sub-ranges within.
  const buildProposed = () => windows
    .filter(w => w.date && w.start && w.end && w.start < w.end)
    .map((w, i) => ({ id: `w${i}_${w.date}_${w.start}`, date: w.date, start: w.start, end: w.end }));

  const proposedCount = buildProposed().length;

  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    const proposed = buildProposed();
    if (proposed.length === 0) { alert('Add at least one valid window (date + start before end).'); return; }
    setSaving(true);
    try {
      const tz = timezone || localTimezone();
      const resp = await fetch(`${API}/api/availability`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ candidate_id: candidateId, pipeline_id: pipelineId, title, mode: 'ranges', timezone: tz, proposed }),
      });
      const row = await resp.json();
      setCreated(row);
      onCreated?.(row);
    } catch (e) { alert('Failed to create request'); } finally { setSaving(false); }
  };

  const link = created ? `${window.location.origin}/availability/${created.token}` : '';
  const copyLink = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const cardInner = (
      <div className={embedded ? "flex flex-col" : "relative bg-white rounded shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"}>
        <div className={embedded
          ? "flex-shrink-0 flex items-center justify-between mb-1"
          : "flex-shrink-0 px-4 h-11 border-b border-gray-200 flex items-center justify-between bg-gray-50 rounded-t"}>
          <h3 className={embedded ? "text-[11px] font-semibold text-gray-600 uppercase tracking-wide" : "text-sm font-semibold text-gray-900"}>Request availability</h3>
          <button onClick={onClose} className="p-0.5 text-gray-400 hover:text-gray-700"><svg className={embedded ? "w-4 h-4" : "w-5 h-5"} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>

        {created ? (
          <div className={embedded ? "space-y-3" : "flex-1 overflow-y-auto p-5"}>
            <div className="text-center py-3">
              <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              </div>
              <h4 className="text-sm font-semibold text-gray-900">Link ready to share</h4>
              <p className="text-xs text-gray-500 mt-1">Send this to the candidate — they'll pick from your proposed times.</p>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input readOnly value={link} onFocus={(e) => e.target.select()}
                className="flex-1 text-xs border border-gray-300 rounded px-3 py-2 bg-gray-50 text-gray-700" />
              <button onClick={copyLink}
                className="px-3 py-2 text-xs font-medium text-white bg-emerald-600 rounded hover:bg-emerald-700 flex-shrink-0">
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        ) : (
        <div className={embedded ? "space-y-3" : "flex-1 overflow-y-auto p-5 space-y-4"}>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Meeting timezone</span>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)}
              className="mt-1 w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500">
              {!COMMON_TIMEZONES.some(t => t.value === timezone) && <option value={timezone}>{tzLabel(timezone)}</option>}
              {COMMON_TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span className="text-[10px] text-gray-400 mt-0.5 block">Your windows are in this timezone; the candidate sees them converted to theirs.</span>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">Interviewer windows</span>
              <button onClick={addWindow} className="text-[11px] text-emerald-700 hover:text-emerald-800 font-medium">+ Add window</button>
            </div>
            <div className="space-y-2">
              {windows.map(w => (
                <div key={w.id} className="flex items-center gap-2">
                  <input type="date" value={w.date} onChange={(e) => updWindow(w.id, { date: e.target.value })}
                    className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                  <input type="time" value={w.start} onChange={(e) => updWindow(w.id, { start: e.target.value })}
                    className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                  <span className="text-gray-400 text-xs">to</span>
                  <input type="time" value={w.end} onChange={(e) => updWindow(w.id, { end: e.target.value })}
                    className="text-xs border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                  {windows.length > 1 && (
                    <button onClick={() => delWindow(w.id)} className="p-1 text-gray-300 hover:text-red-500"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-gray-400">
            {proposedCount} availability window{proposedCount === 1 ? '' : 's'} — the candidate will mark their free times within these.
          </p>
        </div>
        )}

        <div className={embedded ? "flex items-center justify-end gap-2 mt-3" : "flex-shrink-0 px-5 h-14 border-t border-gray-200 flex items-center justify-end gap-2"}>
          {created ? (
            <button onClick={onClose} className="h-[30px] px-2.5 text-sm border rounded bg-emerald-50 border-emerald-300 text-emerald-700 font-medium hover:bg-emerald-100">Done</button>
          ) : (
            <>
              <button onClick={onClose} className="h-[30px] px-2.5 text-sm border rounded bg-white border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={create} disabled={saving || proposedCount === 0}
                className="h-[30px] px-2.5 text-sm border rounded bg-emerald-50 border-emerald-300 text-emerald-700 font-medium hover:bg-emerald-100 disabled:opacity-50">
                {saving ? 'Creating…' : 'Create & get link'}
              </button>
            </>
          )}
        </div>
      </div>
  );

  if (embedded) return cardInner;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {cardInner}
    </div>
  );
}
